/**
 * lessonLayout – the authored layout of a lesson room, and where every model,
 * the desk and the lesson panel go under it.
 *
 * `workspace.ts` answers "where is the room" (one anchor). This answers "how is
 * it arranged": which of four presets a lesson uses, how big each model is, and
 * where each one stands so that
 *
 *   - no two models ever overlap, at rest or fully exploded;
 *   - the lesson's main model is centred and, where the preset calls for it,
 *     larger than the ones supporting it;
 *   - models can keep their true size ratios (a nucleus smaller than its cell),
 *     with a readable floor, and say so when the floor bends the truth;
 *   - everything sits inside a comfortable viewing cone, seated or standing.
 *
 * Pure geometry, like `workspace.ts`: an anchor and a layout in, positions out.
 * The Studio preview draws from the same function, so what an editor sees is
 * what the room builds.
 */

import * as THREE from 'three';

import { DESK_DROP, PANEL_DROP } from './ergonomics';
import {
  PANEL_ANGULAR_WIDTH_DEG,
  PANEL_ASPECT,
  PANEL_BEARING_LIMIT_DEG,
  bearing,
  bearingTowards,
  yawTowards,
  type ModelSlot,
  type WorkspaceAnchor,
  type WorkspaceFrame,
} from './workspace';
import { THEME_IDS } from './roomTheme';

export type LayoutPreset = 'lecture' | 'lab_bench' | 'focus' | 'gallery';
export type PanelSide = 'auto' | 'left' | 'right';

export interface LessonLayout {
  preset: LayoutPreset;
  /** `auto` takes the preset's own side. Gallery always centres the panel. */
  panelSide: PanelSide;
  /** Multiplies the size budget, 0.7–1.5. */
  modelScale: number;
  /** Desk distance in metres, 0.6–0.95. Overrides the preset's own. */
  deskReach: number | null;
  /** The lesson's main model. Defaults to the first. */
  primaryAssetId: string | null;
  /** Keep true size ratios between models, from `assetSizesCm`. */
  realScale: boolean;
  /** Real largest dimension of each model, in centimetres, by asset id. */
  assetSizesCm: Record<string, number>;
  /** The room around the desk: 'auto' follows the subject (roomTheme.ts), or a theme id. */
  roomTheme: string;
  /** Themed pieces around the desk (rocks, bookshelf, glassware…). */
  showProps: boolean;
  /** Bumped by "Shuffle" for a new arrangement of this lesson's room. */
  propSeed: number;
}

export const LAYOUT_PRESETS: LayoutPreset[] = ['lecture', 'lab_bench', 'focus', 'gallery'];

interface PresetSpec {
  label: string;
  purpose: string;
  side: 'left' | 'right' | 'center';
  reach: number;
  /** Widest the model arc may spread, in degrees. */
  maxSpreadDeg: number;
  primaryWeight: number;
  secondaryWeight: number;
  panelDistance: number;
  panelWidthDeg: number;
  /** Panel centre relative to the eye, metres (negative = below). */
  panelLift: number;
}

export const PRESET_SPECS: Record<LayoutPreset, PresetSpec> = {
  lecture: {
    label: 'Lecture',
    purpose: 'Read the panel, then look at the models. The default.',
    side: 'left',
    reach: 0.7,
    maxSpreadDeg: 80,
    primaryWeight: 1.15,
    secondaryWeight: 1,
    panelDistance: 2.0,
    panelWidthDeg: PANEL_ANGULAR_WIDTH_DEG,
    panelLift: -PANEL_DROP,
  },
  lab_bench: {
    label: 'Lab bench',
    purpose: 'Compare several apparatus or specimens side by side, at equal size.',
    side: 'right',
    reach: 0.8,
    maxSpreadDeg: 90,
    primaryWeight: 1,
    secondaryWeight: 1,
    panelDistance: 2.0,
    panelWidthDeg: PANEL_ANGULAR_WIDTH_DEG,
    panelLift: -PANEL_DROP,
  },
  focus: {
    label: 'Focus',
    purpose: 'One main object (a heart, an atom, a cell) with smaller supporting models.',
    side: 'left',
    reach: 0.75,
    maxSpreadDeg: 80,
    primaryWeight: 1.6,
    secondaryWeight: 0.6,
    panelDistance: 2.0,
    panelWidthDeg: PANEL_ANGULAR_WIDTH_DEG,
    panelLift: -PANEL_DROP,
  },
  gallery: {
    label: 'Gallery',
    purpose: 'Many peer models (planets, shapes, organelles) under a panel straight ahead.',
    side: 'center',
    reach: 0.75,
    maxSpreadDeg: 80,
    primaryWeight: 1,
    secondaryWeight: 1,
    panelDistance: 2.3,
    panelWidthDeg: 32,
    panelLift: 0.05,
  },
};

export const DEFAULT_LESSON_LAYOUT: LessonLayout = {
  preset: 'lecture',
  panelSide: 'auto',
  modelScale: 1,
  deskReach: null,
  primaryAssetId: null,
  realScale: false,
  assetSizesCm: {},
  roomTheme: 'auto',
  showProps: true,
  propSeed: 0,
};

/** Largest dimension of a model before any preset weighting, in metres. Today's size. */
export const BASE_MODEL_SIZE = 0.25;
/** Smallest a model is drawn, however small true scale would make it. */
export const MIN_READABLE_SIZE = 0.06;
/** Clear space between two models' footprints. */
export const MODEL_GAP = 0.05;
/** A footprint is at most a square of the model's largest dimension; this circle bounds it. */
export const FOOTPRINT_RADIUS_FACTOR = Math.SQRT1_2;
/** Room reserved on each side of a model for exploding it, as a fraction of its size. */
export const EXPLODE_ROOM = 0.35;
/** The least explode room a crowded desk keeps before shrinking the models further. */
export const MIN_EXPLODE_ROOM = 0.2;
/** Comfortable limits, in degrees. */
export const MAX_MODEL_BEARING_DEG = 45;
export const MIN_MODEL_PITCH_DEG = 15;
export const MAX_MODEL_PITCH_DEG = 40;
export const MAX_PANEL_TOP_DEG = 10;

const REACH_RANGE: [number, number] = [0.6, 0.95];
const SCALE_RANGE: [number, number] = [0.7, 1.5];
const SIZE_CM_RANGE: [number, number] = [1e-4, 1e9];

const clamp = (v: number, [lo, hi]: [number, number]) => Math.min(hi, Math.max(lo, v));
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * The layout a lesson actually uses: the topic's own values over the global
 * default over the built-in one, every number clamped, anything unknown dropped.
 * Whatever is stored, the room still builds.
 */
export function resolveLessonLayout(topic?: unknown, globalDefault?: unknown): LessonLayout {
  const layers = [globalDefault, topic].filter(
    (l): l is Record<string, unknown> => !!l && typeof l === 'object'
  );
  const out: LessonLayout = { ...DEFAULT_LESSON_LAYOUT, assetSizesCm: {} };
  for (const l of layers) {
    if (LAYOUT_PRESETS.includes(l.preset as LayoutPreset)) out.preset = l.preset as LayoutPreset;
    if (l.panelSide === 'auto' || l.panelSide === 'left' || l.panelSide === 'right') out.panelSide = l.panelSide;
    if (finite(l.modelScale)) out.modelScale = clamp(l.modelScale, SCALE_RANGE);
    if (l.deskReach === null) out.deskReach = null;
    else if (finite(l.deskReach)) out.deskReach = clamp(l.deskReach, REACH_RANGE);
    if (typeof l.realScale === 'boolean') out.realScale = l.realScale;
    if (typeof l.roomTheme === 'string' && (l.roomTheme === 'auto' || (THEME_IDS as readonly string[]).includes(l.roomTheme))) {
      out.roomTheme = l.roomTheme;
    }
    if (typeof l.showProps === 'boolean') out.showProps = l.showProps;
    // Per-lesson only: a default cannot know which models a lesson has.
    if (l === topic) {
      if (typeof l.primaryAssetId === 'string' && l.primaryAssetId) out.primaryAssetId = l.primaryAssetId;
      if (finite(l.propSeed)) out.propSeed = Math.max(0, Math.floor(l.propSeed));
      if (l.assetSizesCm && typeof l.assetSizesCm === 'object') {
        for (const [id, cm] of Object.entries(l.assetSizesCm as Record<string, unknown>)) {
          if (finite(cm) && cm > 0) out.assetSizesCm[id] = clamp(cm, SIZE_CM_RANGE);
        }
      }
    }
  }
  return out;
}

export interface PlannedSlot extends ModelSlot {
  assetId: string;
  /** Largest dimension the model is fitted to, metres. */
  targetSize: number;
  /** Fraction of a full explode that still stays inside this model's room, 0–1. */
  explodeReach: number;
  row: 0 | 1;
  primary: boolean;
}

export interface PlannedFrame extends WorkspaceFrame {
  slots: PlannedSlot[];
  layout: LessonLayout;
  /** True scale was asked for but a model had to be enlarged to stay readable. */
  notToScale: boolean;
  rows: 1 | 2;
  panelBearingDeg: number;
  panelWidthDeg: number;
}

export interface PlanOptions {
  /** A flat screen: the panel is fitted to what the camera shows. */
  flat?: { panelBearingDeg: number; panelWidthDeg: number } | null;
}

/** The side the panel is on, after `auto` and the preset. */
export function panelSideOf(layout: LessonLayout): 'left' | 'right' | 'center' {
  const spec = PRESET_SPECS[layout.preset];
  if (spec.side === 'center') return 'center';
  return layout.panelSide === 'auto' ? spec.side : layout.panelSide;
}

/** Half the angle a model needs on an arc of radius `r`, including its gap. */
function halfAngle(radius: number, halfWidth: number): number {
  return 2 * Math.asin(Math.min(1, halfWidth / (2 * radius)));
}

interface RowPlan {
  angles: number[]; // bearings in degrees, in the order given
  span: number; // twice the wider side, so the row fits ±span/2
}

/**
 * Pack models along one arc, edge to edge, the first in the middle and the rest
 * alternating right and left of it.
 *
 * The row is centred on 0, which is the tightest fit. The first in a row is the
 * one that matters most (the main model in the front row), so the row is then
 * turned to put it straight ahead, if that still fits inside `maxSpread` — or
 * always, when `centreFirst` (a preset that emphasises the main model), in
 * which case the fit is judged on the turned row.
 */
function packRow(radius: number, halfWidths: number[], maxSpread = Infinity, centreFirst = false): RowPlan {
  const half = halfWidths.map((h) => THREE.MathUtils.radToDeg(halfAngle(radius, h)));
  // Linear order left to right: ...4, 2, 0, 1, 3...
  const order = half.map((_, i) => i).sort((a, b) => sideKey(a) - sideKey(b));
  const total = half.reduce((a, h) => a + 2 * h, 0);
  const angles: number[] = new Array(half.length);
  let cursor = -total / 2;
  for (const i of order) {
    angles[i] = cursor + half[i];
    cursor += 2 * half[i];
  }
  const extent = (shift: number) =>
    2 * Math.max(0, ...angles.map((a, i) => Math.abs(a - shift) + half[i]));
  const shift = angles.length > 0 && (centreFirst || extent(angles[0]) <= maxSpread) ? angles[0] : 0;
  return { angles: angles.map((a) => a - shift), span: extent(shift) };
}

/** Position of item `i` in the left-to-right order ...4, 2, 0, 1, 3... */
function sideKey(i: number): number {
  return i % 2 === 1 ? (i + 1) / 2 : -i / 2;
}

/**
 * Every position in the room for `assetIds`, under `layout`.
 */
export function planWorkspace(
  anchor: WorkspaceAnchor,
  layout: LessonLayout,
  assetIds: string[],
  opts: PlanOptions = {}
): PlannedFrame {
  const spec = PRESET_SPECS[layout.preset];
  const eyeY = anchor.groundY + anchor.eyeHeight;
  const deskY = Math.max(anchor.groundY + 0.3, eyeY - DESK_DROP);
  const drop = eyeY - deskY;

  // Reach: authored or the preset's, then held inside the comfortable pitch.
  const pitchReach = (deg: number) => drop / Math.tan(THREE.MathUtils.degToRad(deg));
  const reach = Math.min(
    pitchReach(MIN_MODEL_PITCH_DEG),
    Math.max(pitchReach(MAX_MODEL_PITCH_DEG), layout.deskReach ?? spec.reach)
  );
  const maxSpread = Math.min(spec.maxSpreadDeg, 2 * MAX_MODEL_BEARING_DEG);
  // Strong emphasis (Focus) always centres the main model; mild emphasis only when it fits.
  const centreFirst = spec.primaryWeight >= 1.5 * spec.secondaryWeight;

  // 1. Sizes, before fitting.
  const primaryId =
    layout.primaryAssetId && assetIds.includes(layout.primaryAssetId) ? layout.primaryAssetId : assetIds[0];
  const ordered = primaryId ? [primaryId, ...assetIds.filter((id) => id !== primaryId)] : [];
  const budget = BASE_MODEL_SIZE * layout.modelScale;
  const known = ordered.map((id) => layout.assetSizesCm[id]).filter((v): v is number => finite(v) && v > 0);
  const useReal = layout.realScale && known.length > 0;
  const largestReal = useReal ? Math.max(...known) : 1;
  const base = ordered.map((id, i) => {
    const weight = i === 0 ? spec.primaryWeight : spec.secondaryWeight;
    const cm = layout.assetSizesCm[id];
    if (useReal) {
      // True ratios between the models whose size is known; the rest keep the budget.
      return finite(cm) && cm > 0 ? budget * spec.primaryWeight * (cm / largestReal) : budget * weight;
    }
    return budget * weight;
  });

  // 2. Find the arrangement that shrinks the models least.
  //    Relaxations in order: generous explode room, less of it, a second row
  //    (one row reads better, so it is kept while the models stay reasonably
  //    big), then a second row with the least room. Some explode room is always
  //    kept: taking a model apart is how most of these lessons teach.
  // `minK`: the least an arrangement may shrink the models and still be taken.
  const tries: { rows: 1 | 2; room: number; minK: number }[] = [
    { rows: 1, room: EXPLODE_ROOM, minK: 0.75 },
    { rows: 1, room: MIN_EXPLODE_ROOM, minK: 0.55 },
    { rows: 2, room: MIN_EXPLODE_ROOM, minK: 0.6 },
    { rows: 2, room: MIN_EXPLODE_ROOM / 2, minK: 0 },
  ];

  const floorOf = (s: number) => Math.max(s, MIN_READABLE_SIZE);
  /**
   * A back row keeps the space straight behind the main model empty: the main
   * model stands front and centre, and anything behind it would be hidden. The
   * gap is the main model's own width, widened to the same angle at the back row.
   */
  const backGap = (mainHalfWidth: number, backReach: number) => (mainHalfWidth * backReach) / reach;

  const split = (n: number) => {
    // The main model is always in the front row.
    const front: number[] = [];
    const back: number[] = [];
    for (let i = 0; i < n; i += 1) (i % 2 === 0 ? front : back).push(i);
    return { front, back };
  };

  interface Arrangement {
    rows: 1 | 2;
    room: number;
    k: number;
    backReach: number;
  }

  const layoutFits = (rows: 1 | 2, room: number, sizes: number[]) => {
    const hw = (s: number) => s * (FOOTPRINT_RADIUS_FACTOR + room) + MODEL_GAP / 2;
    if (rows === 1) {
      return { fits: packRow(reach, sizes.map(hw), maxSpread, centreFirst).span <= maxSpread, backReach: reach };
    }
    const { front, back } = split(sizes.length);
    const maxR = (idx: number[]) => Math.max(0, ...idx.map((i) => hw(sizes[i])));
    // Rows far enough apart that no front and back footprint can touch.
    const backReach = reach + Math.max(0.3, maxR(front) + maxR(back));
    const fits =
      packRow(reach, front.map((i) => hw(sizes[i])), maxSpread, centreFirst).span <= maxSpread &&
      packRow(backReach, [backGap(hw(sizes[0]), backReach), ...back.map((i) => hw(sizes[i]))], maxSpread, true).span <=
        maxSpread &&
      THREE.MathUtils.radToDeg(Math.atan(drop / backReach)) >= MIN_MODEL_PITCH_DEG;
    return { fits, backReach };
  };

  const bestK = (rows: 1 | 2, room: number): Arrangement | null => {
    if (ordered.length === 0) return { rows, room, k: 1, backReach: reach };
    if (rows === 2 && ordered.length < 2) return null;
    const at = (k: number) => layoutFits(rows, room, base.map((s) => floorOf(s * k)));
    const full = at(1);
    if (full.fits) return { rows, room, k: 1, backReach: full.backReach };
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 30; i += 1) {
      const mid = (lo + hi) / 2;
      if (at(mid).fits) lo = mid;
      else hi = mid;
    }
    if (lo <= 0) return null;
    return { rows, room, k: lo, backReach: at(lo).backReach };
  };

  let chosen: Arrangement | null = null;
  let fallback: Arrangement | null = null;
  for (const t of tries) {
    const a = bestK(t.rows, t.room);
    if (!a) continue;
    if (a.k >= t.minK) {
      chosen = a;
      break;
    }
    if (!fallback || a.k > fallback.k) fallback = a;
  }

  let sizes: number[];
  let arrangement: Arrangement;
  if (chosen || fallback) {
    arrangement = (chosen ?? fallback)!;
    sizes = base.map((s) => floorOf(s * arrangement.k));
  } else {
    // Even the readable floor does not fit (a great many models): give up the
    // floor rather than overlap. Never overlapping outranks legibility.
    arrangement = { rows: 2, room: 0, k: 1, backReach: reach };
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 40; i += 1) {
      const mid = (lo + hi) / 2;
      if (layoutFits(2, 0, base.map((s) => s * mid)).fits) lo = mid;
      else hi = mid;
    }
    sizes = base.map((s) => s * lo);
    arrangement.backReach = layoutFits(2, 0, sizes).backReach;
  }
  const notToScale = useReal && base.some((s, i) => sizes[i] > s * arrangement.k + 1e-9);

  // 3. Positions.
  const room = arrangement.room;
  const hw = (s: number) => s * (FOOTPRINT_RADIUS_FACTOR + room) + MODEL_GAP / 2;
  const rowsOf =
    arrangement.rows === 1
      ? { front: ordered.map((_, i) => i), back: [] as number[] }
      : split(ordered.length);

  const slotsByIndex: PlannedSlot[] = [];
  const placeRow = (indices: number[], radius: number, row: 0 | 1) => {
    const widths = indices.map((i) => hw(sizes[i]));
    const plan =
      row === 0
        ? packRow(radius, widths, maxSpread, centreFirst)
        : // The empty space behind the main model goes first, at 0; it is not a model.
          (() => {
            const p = packRow(radius, [backGap(hw(sizes[0]), radius), ...widths], maxSpread, true);
            return { ...p, angles: p.angles.slice(1) };
          })();
    indices.forEach((i, j) => {
      const direction = bearing(anchor.forward, plan.angles[j]);
      const position = anchor.position.clone().addScaledVector(direction, radius);
      position.y = deskY;
      const footprint = sizes[i] * FOOTPRINT_RADIUS_FACTOR;
      // The room around it: its own half-width less its resting footprint.
      const free = hw(sizes[i]) - MODEL_GAP / 2 - footprint;
      slotsByIndex[i] = {
        position,
        yaw: yawTowards(position, anchor.position),
        assetId: ordered[i],
        targetSize: sizes[i],
        explodeReach: Math.min(1, Math.max(0, free / sizes[i])),
        row,
        primary: i === 0,
      };
    });
  };
  placeRow(rowsOf.front, reach, 0);
  placeRow(rowsOf.back, arrangement.backReach, 1);

  // Back to the caller's order, so slot i is asset i.
  const slots = assetIds.map((id) => slotsByIndex[ordered.indexOf(id)]);

  // The models' angular extent, for clearing the panel.
  let leftEdge = 0;
  let rightEdge = 0;
  for (const s of slots) {
    const b = bearingTowards(anchor.forward, anchor.position, s.position);
    const r = Math.hypot(s.position.x - anchor.position.x, s.position.z - anchor.position.z);
    const edge = THREE.MathUtils.radToDeg(Math.asin(Math.min(1, (s.targetSize * FOOTPRINT_RADIUS_FACTOR) / r)));
    leftEdge = Math.min(leftEdge, b - edge);
    rightEdge = Math.max(rightEdge, b + edge);
  }

  // 4. Dock: under both rows, with a margin.
  const frontHalfChord = Math.max(Math.abs(leftEdge), Math.abs(rightEdge));
  const dockDepth =
    arrangement.rows === 2 ? arrangement.backReach - reach + 0.5 : 0.5;
  const dockCenter = anchor.position
    .clone()
    .addScaledVector(anchor.forward, reach + (dockDepth - 0.5) / 2);
  dockCenter.y = deskY;
  const dockReach = arrangement.rows === 2 ? arrangement.backReach : reach;
  const dock = {
    center: dockCenter,
    width: Math.max(0.6, 2 * dockReach * Math.sin(THREE.MathUtils.degToRad(Math.min(89, frontHalfChord))) + 0.4),
    depth: dockDepth,
    yaw: yawTowards(dockCenter, anchor.position),
  };

  // 5. Panel.
  const side = panelSideOf(layout);
  const flat = opts.flat ?? null;
  let panelWidthDeg = flat ? Math.min(flat.panelWidthDeg, spec.panelWidthDeg) : spec.panelWidthDeg;
  let panelBearingDeg: number;
  if (side === 'center') {
    panelBearingDeg = 0;
  } else if (flat) {
    // Fitted to the screen; the right-hand presets mirror it.
    panelBearingDeg = side === 'left' ? flat.panelBearingDeg : -flat.panelBearingDeg;
  } else {
    // Clear of the models' outer edge by the same margin models keep from each other.
    const clearance = 4;
    const edge = side === 'left' ? -leftEdge : rightEdge;
    const need = edge + clearance + panelWidthDeg / 2;
    const magnitude = Math.min(-PANEL_BEARING_LIMIT_DEG, Math.max(45, need));
    panelBearingDeg = side === 'left' ? -magnitude : magnitude;
  }

  const panelDirection = bearing(anchor.forward, panelBearingDeg);
  const panelDistance = spec.panelDistance;
  const panelPosition = anchor.position.clone().addScaledVector(panelDirection, panelDistance);
  let panelWidth = 2 * panelDistance * Math.tan(THREE.MathUtils.degToRad(panelWidthDeg / 2));
  let panelHeight = panelWidth * PANEL_ASPECT;
  // Top edge no higher than MAX_PANEL_TOP_DEG above the eye.
  const maxTop = panelDistance * Math.tan(THREE.MathUtils.degToRad(MAX_PANEL_TOP_DEG));
  let lift = spec.panelLift;
  if (lift + panelHeight / 2 > maxTop) {
    lift = maxTop - panelHeight / 2;
  }
  // Centre-panel presets: keep the bottom edge above the models' tops as seen
  // from the eye, so the panel never sits in front of them.
  if (side === 'center' && slots.length > 0) {
    // The highest a model top reaches in view: a far back-row model counts too.
    const modelTopPitch = Math.max(
      ...slots.map((s) =>
        Math.atan(
          (deskY + s.targetSize - eyeY) /
            Math.hypot(s.position.x - anchor.position.x, s.position.z - anchor.position.z)
        )
      )
    );
    const minBottom = panelDistance * Math.tan(modelTopPitch + THREE.MathUtils.degToRad(2));
    if (lift - panelHeight / 2 < minBottom) {
      // Shrink the panel rather than raise it past the comfortable top.
      const room = maxTop - minBottom;
      if (room < panelHeight) {
        panelHeight = Math.max(0.3, room);
        panelWidth = panelHeight / PANEL_ASPECT;
        panelWidthDeg = THREE.MathUtils.radToDeg(2 * Math.atan(panelWidth / (2 * panelDistance)));
      }
      lift = minBottom + panelHeight / 2;
    }
  }
  panelPosition.y = eyeY + lift;

  return {
    anchor,
    deskY,
    dock,
    slots,
    panel: {
      position: panelPosition,
      width: panelWidth,
      height: panelHeight,
      yaw: yawTowards(panelPosition, anchor.position),
    },
    layout,
    notToScale,
    rows: arrangement.rows,
    panelBearingDeg,
    panelWidthDeg,
  };
}
